import React from 'react';
import { useTranslation } from 'react-i18next';

import dayjs from 'shared/helpers/dayjs';
import useWindowSize from 'shared/hooks/useWindowResize';
import breakpoints from 'theme/atoms/breakpoints';
import { Arrow } from 'icons/untitledui';

import * as Styled from 'events/styles/DateNavHeader.style';

import WeekWidget from './WeekWidget';

interface DateNavHeaderProps {
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
}

export default function DateNavHeader({
  selectedDate,
  onSelectDate,
}: DateNavHeaderProps): JSX.Element {
  const { t } = useTranslation('pageEvents');
  const [windowWidth] = useWindowSize();
  const isDesktopView = windowWidth > parseInt(breakpoints.laptop, 10);

  if (!isDesktopView) {
    return (
      <WeekWidget selectedDate={selectedDate} onSelectDate={onSelectDate} />
    );
  }

  const today = dayjs().startOf('day');
  const current = dayjs(selectedDate).startOf('day');
  const isToday = current.isSame(today, 'day');
  const isYesterday = current.isSame(today.subtract(1, 'day'), 'day');
  const isFutureDisabled = !current.isBefore(today, 'day');

  let label = current.format('ddd, MMM D');
  if (isToday) label = t('eventsHome.today');
  else if (isYesterday) label = t('eventsHome.yesterday');

  const handlePrev = () => onSelectDate(current.subtract(1, 'day').toDate());
  const handleNext = () => {
    if (isFutureDisabled) return;
    onSelectDate(current.add(1, 'day').toDate());
  };

  return (
    <Styled.Container>
      <Styled.ArrowButton
        type="button"
        onClick={handlePrev}
        aria-label={t('eventsHome.previousDay')}
      >
        <Arrow.ChevronLeft />
      </Styled.ArrowButton>
      <Styled.Title>{label}</Styled.Title>
      <Styled.ArrowButton
        type="button"
        onClick={handleNext}
        disabled={isFutureDisabled}
        aria-label={t('eventsHome.nextDay')}
      >
        <Arrow.ChevronRight />
      </Styled.ArrowButton>
    </Styled.Container>
  );
}
